import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { colors, typography, spacing } from '../theme';
import { httpClient } from '../services/http.client';
import { HISTORY_ENDPOINTS } from '../services/api.constants';
import { KeycloakService } from '../services/keycloak.service';
import { asyncAnalysisService } from '../services/async-analysis.service';
import { TechniqueDefinitionsService, type TechniqueDefinition } from '../services/technique-definitions.service';
import { enumLabel } from '../utils/i18n-enums';
import { Verdict } from '../components/PipelineAnalysis/sections/Verdict';
import { InputPreview } from '../components/PipelineAnalysis/sections/InputPreview';
import { TechniquesResults } from '../components/PipelineAnalysis/sections/TechniquesResults';
import { AiResults, toAiDisplayResult } from '../components/PipelineAnalysis/sections/AiResults';
import { ClaimsResults } from '../components/PipelineAnalysis/sections/ClaimsResults';
import { SourceResults } from '../components/PipelineAnalysis/sections/SourceResults';
import { DownloadPdfButtonLazy } from '../components/Reports/DownloadPdfButtonLazy';
import { VerdictSection } from '../components/PipelineAnalysis/styles';
import type {
  VerdictResult, TechniquesResult, AiTamperedResult, ClaimsResult, SourceAssessmentResult,
} from '../types/analysis-session';
import type { ComponentStatus } from '../components/PipelineAnalysis/types';

// =============================================================================
// Types — aligned with backend /api/v3/pipeline/history responses
// =============================================================================

/** GET /api/v3/pipeline/history list item — flat AnalysisSession with null components */
interface HistoryItem {
  session_id: string;
  user_id: string;
  input_type: string;
  input_text: string | null;
  input_url: string | null;
  input_media_url: string | null;
  status: string;
  risk_score: number | null;
  risk_category: string | null;
  risk_level: string | null;
  confidence: number | null;
  confidence_level: string | null;
  components_run: string[];
  started_at: string;
  completed_at: string | null;
  total_duration_ms: number | null;
  created_at: string;
  techniques: null;
  ai_tampered: null;
  claims: null;
  domain: null;
  source_assessment: null;
  verdict: null;
  // Flat source fields from list API
  source_trust_score?: number | null;
  source_verdict?: string | null;
  source_publication?: string | null;
  source_author?: string | null;
  source_platform?: string | null;
}

interface HistoryListResponse {
  success: boolean;
  data: {
    items: HistoryItem[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

/** GET /api/v3/pipeline/history/:sessionId — full flat AnalysisSession */
interface HistoryDetailData {
  session_id: string;
  user_id: string;
  input_type: string;
  input_text: string | null;
  input_url: string | null;
  input_media_url: string | null;
  status: string;
  components_run: string[];
  components_skipped: string[];
  risk_score: number | null;
  risk_category: string | null;
  risk_level: string | null;
  total_duration_ms: number | null;
  started_at: string;
  completed_at: string | null;
  techniques: {
    manipulation_score: number;
    techniques_count: number;
    techniques_detected: {
      name: string;
      dimension: string;
      subdimension: string;
      severity: number;
      confidence: number;
      evidence: string;
    }[];
    total_duration_ms: number;
  } | null;
  ai_tampered: {
    ai_probability: number;
    verdict: string;
    indicators_count: number;
    disclosure_detected: boolean;
    disclosure_text: string | null;
    indicators_detected: {
      id: string;
      name: string;
      category: string;
      confidence: number;
      evidence: string;
    }[];
    image_analysis: {
      indicators: string[];
      evidence: string;
      model_used: string;
    } | null;
    total_duration_ms: number;
  } | null;
  claims: {
    total_claims: number;
    verified_true: number;
    verified_false: number;
    unverified: number;
    opinions: number;
    credibility_score: number;
    claims_verified: {
      text: string;
      status: string;
      status_name: string;
      status_color: string;
      type_name: string;
      sources: { url: string; stance: string; reliability?: string }[];
      reasoning: string;
    }[];
    total_duration_ms: number;
  } | null;
  domain: {
    domain: string;
    verdict: string;
    trust_score: number;
    risk_level: string;
    age_days: number;
    is_blacklisted: boolean;
    reputation_score: number;
    has_ssl: boolean;
    ssl_valid: boolean;
    ssl_issuer: string;
    registrar: string;
    red_flags: string[];
    warnings: string[];
  } | null;
  source_assessment: {
    trust_score: number;
    verdict: string;
    risk_level: string;
    publication: { name: string | null; source_type: string; score: number; confirmed: boolean };
    author: { name: string | null; classification: string; score: number; confirmed: boolean; credibility_indicators: string[] };
    platform: { code: string; name: string; score: number };
    domain: { name: string | null; age_days: number | null; score: number; has_ssl: boolean | null; is_blacklisted: boolean; organization: string | null; country: string | null; red_flags: string[] };
    formula: { breakdown: string };
    warnings: string[];
    red_flags: string[];
    duration_ms: number;
  } | null;
  verdict: {
    risk_score: number;
    risk_category: string;
    risk_level: string;
    confidence: number;
    confidence_level: string;
    recommended_action: string;
    score_manipulation: number | null;
    score_claims: number | null;
    score_ai: number | null;
    score_source: number | null;
    override_applied: boolean;
    override_type: string | null;
    override_reason: string | null;
    explanation_ro: string;
    explanation_en: string;
    virality_score?: number;
    virality_level?: string;
    virality_factors?: { factor: string; score: number; weight: number; detail: string }[];
  } | null;
}

interface HistoryDetailResponse {
  success: boolean;
  data: HistoryDetailData;
}

interface HistoryProps {
  onDetailViewChange?: (isOpen: boolean) => void;
}

// =============================================================================
// Constants & Helpers
// =============================================================================

const PAGE_LIMIT = 20;

/** Check if a session status means "still in progress" — whitelist approach to avoid polling everything */
const isRunningStatus = (status: string | null | undefined): boolean =>
  status === 'running' || status === 'processing' || status === 'queued' || status === 'pending';

// Map risk_level -> color
const RISK_LEVEL_COLORS: Record<string, string> = {
  LOW: '#22c55e',
  MODERATE: '#eab308',
  HIGH: '#f97316',
  CRITICAL: '#ef4444',
};
const RISK_CAT_COLORS: Record<string, string> = {
  RELIABLE: '#22c55e',
  MOSTLY_RELIABLE: '#84cc16',
  UNCERTAIN: '#eab308',
  UNRELIABLE: '#f97316',
  DISINFORMATION: '#ef4444',
  // Also support color names from verdict detail
  green: '#22c55e',
  lightgreen: '#84cc16',
  yellow: '#eab308',
  orange: '#f97316',
  red: '#ef4444',
  darkred: '#dc2626',
};

const getRiskColor = (level: string, category?: string) => {
  return RISK_LEVEL_COLORS[level] || RISK_CAT_COLORS[category || ''] || '#94a3b8';
};

const getSeverityColor = (s: number) => s >= 70 ? '#ef4444' : s >= 40 ? '#f97316' : '#eab308';
const getProbColor = (p: number) => p >= 80 ? '#ef4444' : p >= 60 ? '#f97316' : p >= 40 ? '#eab308' : '#22c55e';
const getClaimStatusColor = (status: string) => {
  if (status === 'VT' || status === 'TRUE') return '#22c55e';
  if (status === 'VF' || status === 'FALSE') return '#ef4444';
  return '#94a3b8';
};
const getStanceColor = (s: string) => s === 'SUPPORTS' ? '#22c55e' : s === 'CONTRADICTS' ? '#ef4444' : '#94a3b8';

/** Parse string or number scores defensively, handle null */
const ps = (val: string | number | null | undefined): number => {
  if (val == null) return 0;
  if (typeof val === 'number') return isNaN(val) ? 0 : val;
  const n = parseFloat(val);
  return isNaN(n) ? 0 : n;
};

const getViralityColor = (score: number) =>
  score >= 75 ? '#dc2626' : score >= 50 ? '#ef4444' : score >= 25 ? '#f97316' : '#22c55e';

const formatFactorName = (factor: string) =>
  factor.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const formatTechName = (name: string) => {
  const part = name.includes('.') ? name.split('.').pop()! : name;
  return part.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

// =============================================================================
// Component
// =============================================================================

const History: React.FC<HistoryProps> = ({ onDetailViewChange }) => {
  const { t, i18n } = useTranslation();
  const keycloakUserId = KeycloakService.getUserFromToken()?.sub || '';
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState<HistoryDetailData | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<HistoryListResponse['data']['pagination'] | null>(null);
  const [runningProgress, setRunningProgress] = useState<Record<string, { progress: number; completed_components: string[]; total_components: number; elapsed_ms: number }>>({});
  const [techDefs, setTechDefs] = useState<TechniqueDefinition[]>([]);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const detailPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollFailCountRef = useRef<Record<string, number>>({});

  const loadHistory = useCallback(async () => {
    if (!keycloakUserId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        user_id: keycloakUserId,
        page: page.toString(),
        limit: PAGE_LIMIT.toString(),
      });
      const url = `${HISTORY_ENDPOINTS.LIST}?${params}`;
      const res = await httpClient.get<HistoryListResponse>(url);
      if (res.success && res.data?.items) {
        const statuses = res.data.items.map(i => `${i.session_id.slice(0,8)}=${i.status}`);
        console.log('[History] Item statuses:', statuses);
        setItems(res.data.items);
        setPagination(res.data.pagination || null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [keycloakUserId, page]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  useEffect(() => {
    TechniqueDefinitionsService.getDefinitions()
      .then(setTechDefs)
      .catch(err => console.warn('Failed to load technique definitions:', err));
  }, []);

  // --- Polling for running sessions ---
  const runningSessionIds = items.filter(i => isRunningStatus(i.status)).map(i => i.session_id);

  useEffect(() => {
    if (runningSessionIds.length === 0) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    const pollRunning = async () => {
      for (const sid of runningSessionIds) {
        try {
          const session = await asyncAnalysisService.getQueueStatus(sid);
          // Reset fail counter on success
          pollFailCountRef.current[sid] = 0;
          if (session._queue) {
            setRunningProgress(prev => ({
              ...prev,
              [sid]: {
                progress: session._queue!.progress,
                completed_components: session._queue!.completed_components,
                total_components: session._queue!.total_components,
                elapsed_ms: session._queue!.elapsed_ms,
              },
            }));
          }
          if (session.status === 'completed') {
            try {
              const result = await asyncAnalysisService.getResult(sid);
              if (result.success && result.data) {
                const d = result.data;
                setItems(prev => prev.map(item => {
                  if (item.session_id !== sid) return item;
                  return {
                    ...item,
                    status: 'completed',
                    risk_score: d.risk_score ?? item.risk_score,
                    risk_category: d.risk_category ?? item.risk_category,
                    risk_level: d.risk_level ?? item.risk_level,
                    confidence: d.confidence ?? item.confidence,
                    confidence_level: d.confidence_level ?? item.confidence_level,
                    components_run: d.components_run ?? item.components_run,
                    total_duration_ms: d.total_duration_ms ?? item.total_duration_ms,
                    completed_at: d.completed_at ?? item.completed_at,
                  };
                }));
                setRunningProgress(prev => {
                  const next = { ...prev };
                  delete next[sid];
                  return next;
                });
              }
            } catch (err) { console.warn(`[History] Poll failed for ${sid}:`, err); }
          } else if (session.status === 'failed') {
            setItems(prev => prev.map(item =>
              item.session_id === sid ? { ...item, status: 'failed' } : item
            ));
            setRunningProgress(prev => {
              const next = { ...prev };
              delete next[sid];
              return next;
            });
          }
        } catch (err) {
          // Track consecutive failures — if session is gone from queue (404), stop polling it
          const count = (pollFailCountRef.current[sid] || 0) + 1;
          pollFailCountRef.current[sid] = count;
          if (count >= 3) {
            console.warn(`[History] Session ${sid.slice(0,8)} unreachable after ${count} attempts — marking as stale`);
            setItems(prev => prev.map(item =>
              item.session_id === sid ? { ...item, status: 'failed' } : item
            ));
            setRunningProgress(prev => {
              const next = { ...prev };
              delete next[sid];
              return next;
            });
            delete pollFailCountRef.current[sid];
          } else {
            console.warn(`[History] Poll error for ${sid.slice(0,8)} (attempt ${count}/3):`, err);
          }
        }
      }
    };

    pollRunning();
    pollIntervalRef.current = setInterval(pollRunning, 3000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runningSessionIds.join(',')]);

  // Cleanup detail polling on unmount
  useEffect(() => {
    return () => {
      if (detailPollRef.current) clearInterval(detailPollRef.current);
    };
  }, []);

  const loadDetail = async (sessionId: string) => {
    setSelectedId(sessionId);
    setDetailLoading(true);
    setDetailData(null);
    setDetailError(null);
    // Detail view is now inline (not modal), so sidebar should stay visible.
    // Don't toggle the parent's isDetailViewOpen state.

    try {
      const params = keycloakUserId ? `?user_id=${keycloakUserId}` : '';
      const url = `${HISTORY_ENDPOINTS.DETAIL(sessionId)}${params}`;
      const res = await httpClient.get<HistoryDetailResponse>(url);
      if (res.success && res.data) {
        setDetailData(res.data);
        // Start polling if session is still running
        if (isRunningStatus(res.data.status)) {
          if (detailPollRef.current) clearInterval(detailPollRef.current);
          const detailPollStart = Date.now();
          const DETAIL_POLL_MAX = 300000; // 5 minutes
          let detailPollFails = 0;
          detailPollRef.current = setInterval(async () => {
            // Timeout protection
            if (Date.now() - detailPollStart > DETAIL_POLL_MAX) {
              if (detailPollRef.current) {
                clearInterval(detailPollRef.current);
                detailPollRef.current = null;
              }
              setDetailData(prev => prev ? { ...prev, status: 'failed' } : prev);
              return;
            }
            try {
              const queueData = await asyncAnalysisService.getQueueStatus(sessionId);
              detailPollFails = 0; // reset on success
              if (queueData.status === 'completed') {
                if (detailPollRef.current) {
                  clearInterval(detailPollRef.current);
                  detailPollRef.current = null;
                }
                // Reload full detail from history endpoint
                const p = keycloakUserId ? `?user_id=${keycloakUserId}` : '';
                const u = `${HISTORY_ENDPOINTS.DETAIL(sessionId)}${p}`;
                const r = await httpClient.get<HistoryDetailResponse>(u);
                if (r.success && r.data) {
                  setDetailData(r.data);
                }
              } else if (queueData.status === 'failed') {
                if (detailPollRef.current) {
                  clearInterval(detailPollRef.current);
                  detailPollRef.current = null;
                }
                setDetailData(prev => prev ? { ...prev, status: 'failed' } : prev);
              }
            } catch (err) {
              detailPollFails++;
              console.warn(`[History] Detail poll error (${detailPollFails}/3):`, err);
              if (detailPollFails >= 3) {
                if (detailPollRef.current) {
                  clearInterval(detailPollRef.current);
                  detailPollRef.current = null;
                }
                setDetailData(prev => prev ? { ...prev, status: 'failed' } : prev);
              }
            }
          }, 3000);
        }
      } else {
        setDetailError(`Unexpected response: success=${res.success}, data=${!!res.data}`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[History] Error loading detail:', msg);
      setDetailError(msg);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = useCallback(() => {
    if (detailPollRef.current) {
      clearInterval(detailPollRef.current);
      detailPollRef.current = null;
    }
    setSelectedId(null);
    setDetailData(null);
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedId) closeDetail();
    };
    if (selectedId) {
      document.addEventListener('keydown', handleKey);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    return () => {
      document.removeEventListener('keydown', handleKey);
    };
  }, [selectedId, closeDetail]);

  const handleDelete = async (sessionId: string) => {
    if (deleteConfirm !== sessionId) {
      setDeleteConfirm(sessionId);
      setTimeout(() => setDeleteConfirm(null), 3000);
      return;
    }
    try {
      const params = keycloakUserId ? `?user_id=${keycloakUserId}` : '';
      await httpClient.delete(`${HISTORY_ENDPOINTS.DELETE(sessionId)}${params}`);
      if (selectedId === sessionId) closeDetail();
      setItems(prev => prev.filter(i => i.session_id !== sessionId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting:', err);
    }
  };

  const formatDate = (d: string) => {
    return new Date(d).toLocaleString('ro-RO', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  // Detail data shortcuts
  const verdict = detailData?.verdict;

  // Build props for Verdict component from history detail data
  const buildVerdictProps = (d: HistoryDetailData) => {
    const components: Record<string, ComponentStatus> = {};
    for (const c of d.components_run || []) {
      const key = c === 'domain' && d.source_assessment ? 'source_assessment' : c;
      components[key] = { status: 'completed' };
    }
    for (const c of d.components_skipped || []) {
      const key = c === 'domain' && d.source_assessment ? 'source_assessment' : c;
      components[key] = { status: 'skipped' };
    }
    const fullResult: Record<string, any> = {};
    if (d.techniques) fullResult.techniques = d.techniques;
    if (d.ai_tampered) fullResult.ai_tampered = d.ai_tampered;
    if (d.claims) fullResult.claims = d.claims;
    if (d.domain) fullResult.domain = d.domain;
    if (d.source_assessment) fullResult.source_assessment = d.source_assessment;
    return {
      components,
      fullResult: Object.keys(fullResult).length > 0 ? fullResult : null,
      totalDuration: d.total_duration_ms ?? null,
    };
  };

  // Sort running items to top
  const sortedItems = [...items].sort((a, b) => {
    if (isRunningStatus(a.status) && !isRunningStatus(b.status)) return -1;
    if (!isRunningStatus(a.status) && isRunningStatus(b.status)) return 1;
    return 0;
  });

  // ─── DETAIL VIEW ─── (when an item is selected)
  if (selectedId) {
    return (
      <Container>
        <DetailHeader>
          <BackBtn onClick={closeDetail}>
            <ArrowLeft size={16} strokeWidth={2} />
            <span>{t('history.backToList', 'Înapoi la istoric')}</span>
          </BackBtn>
          {detailData && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {detailData.status === 'completed' && (
                <DownloadPdfButtonLazy sessionId={detailData.session_id} />
              )}
              <DeleteIconBtn
                onClick={() => handleDelete(detailData.session_id)}
                confirm={deleteConfirm === detailData.session_id}
                title={deleteConfirm === detailData.session_id ? t('common.confirmQuestion') : t('common.delete')}
              >
                <Trash2 size={15} strokeWidth={2} />
                <span>{deleteConfirm === detailData.session_id ? t('common.confirmQuestion') : t('common.delete')}</span>
              </DeleteIconBtn>
            </div>
          )}
        </DetailHeader>

        {detailLoading && <StatusMsg>{t('history.loadingResults')}</StatusMsg>}

        {detailError && !detailLoading && (
          <ErrorMsg>{t('history.failedLoadDetails', { error: detailError })}</ErrorMsg>
        )}

        {detailData && !detailLoading && (
          <>
            {/* Input preview — shared component, same as live PipelineAnalysis */}
            <InputPreview
              inputType={detailData.input_type}
              text={detailData.input_text || detailData.input_url || detailData.input_media_url}
            />

            {/* Running indicator */}
            {isRunningStatus(detailData.status) && (
              <RunningDetailBox>
                <CardSpinner size={24} />
                <RunningDetailText>
                  <RunningDetailTitle>{t('history.analysisInProgress')}</RunningDetailTitle>
                  {runningProgress[detailData.session_id] ? (
                    <>
                      <RunningProgressBar>
                        <RunningProgressFill width={runningProgress[detailData.session_id].progress} />
                      </RunningProgressBar>
                      <RunningDetailMeta>
                        {t('history.componentsProgress', { completed: runningProgress[detailData.session_id].completed_components.length, total: runningProgress[detailData.session_id].total_components })}
                        {' · '}{Math.round(runningProgress[detailData.session_id].progress)}%
                        {' · '}{(runningProgress[detailData.session_id].elapsed_ms / 1000).toFixed(0)}s
                      </RunningDetailMeta>
                    </>
                  ) : (
                    <RunningDetailMeta>{t('history.waitingProgress')}</RunningDetailMeta>
                  )}
                </RunningDetailText>
              </RunningDetailBox>
            )}

            {/* Pipeline (multi-component) — full Verdict component, identical to live PipelineAnalysis.
                Single-component sessions are routed below to their dedicated section so that the
                INCONCLUSIVE/low-confidence verdict computed on partial data isn't shown. */}
            {verdict && !isRunningStatus(detailData.status) && (detailData.components_run?.length ?? 0) > 1 && (() => {
              const props = buildVerdictProps(detailData);
              return (
                <Verdict
                  verdict={verdict as VerdictResult}
                  totalDuration={props.totalDuration}
                  components={props.components}
                  fullResult={props.fullResult}
                  techniqueDefinitions={techDefs}
                />
              );
            })()}

            {/* Single-component sessions — render the same editorial layout as the live standalone
                page. We route here whenever components_run has exactly one entry, regardless of
                whether the backend persisted a verdict (which it does, but with INCONCLUSIVE
                category + low confidence because the other 3 components were skipped). */}
            {!isRunningStatus(detailData.status) && (detailData.components_run?.length ?? 0) <= 1 && (() => {
              if (detailData.techniques) {
                return (
                  <VerdictSection>
                    <TechniquesResults
                      result={detailData.techniques as unknown as TechniquesResult}
                      techDefs={techDefs}
                    />
                  </VerdictSection>
                );
              }
              if (detailData.ai_tampered) {
                const display = toAiDisplayResult(
                  detailData.ai_tampered as unknown as AiTamperedResult,
                  { inputType: detailData.input_type, inputText: detailData.input_text },
                );
                return (
                  <VerdictSection>
                    <AiResults result={display} />
                  </VerdictSection>
                );
              }
              if (detailData.claims) {
                return (
                  <VerdictSection>
                    <ClaimsResults
                      result={detailData.claims as unknown as ClaimsResult}
                    />
                  </VerdictSection>
                );
              }
              if (detailData.source_assessment) {
                return (
                  <VerdictSection>
                    <SourceResults
                      result={detailData.source_assessment as unknown as SourceAssessmentResult}
                    />
                  </VerdictSection>
                );
              }
              return <StatusMsg>{t('history.noDataAvailable')}</StatusMsg>;
            })()}
          </>
        )}
      </Container>
    );
  }

  // ─── LIST VIEW ───
  return (
    <Container>
      <PageHeader>
        <PageTitle>{t('history.title')}</PageTitle>
        <RefreshBtn onClick={loadHistory}>{t('common.refresh')}</RefreshBtn>
      </PageHeader>

      {loading && <StatusMsg>{t('history.loadingHistory')}</StatusMsg>}
      {error && <ErrorMsg>{error}</ErrorMsg>}

      {!loading && !error && items.length === 0 && (
        <StatusMsg>{t('history.emptyState')}</StatusMsg>
      )}

      {!loading && items.length > 0 && (
        <>
          <ItemsList>
            {sortedItems.map(item => {
              const isRunning = isRunningStatus(item.status);
              const progress = runningProgress[item.session_id];
              const componentsRun = item.components_run || [];
              const isSingle = componentsRun.length === 1;
              const singleComp = isSingle ? componentsRun[0] : null;

              // Card-level summary: routed by session type so single-component sessions don't
              // surface the bogus INCONCLUSIVE/low-confidence verdict the backend computes
              // from partial data.
              let badgeScore: number;
              let label: string;
              let badgeColor: string;
              let labelColor: string;

              if (isSingle && singleComp === 'domain') {
                badgeScore = item.source_trust_score ?? 0;
                label = item.source_verdict
                  ? enumLabel(item.source_verdict).replace(/_/g, ' ')
                  : t('pipeline.componentNames.source', 'Source');
                badgeColor = badgeScore >= 70 ? '#22c55e'
                  : badgeScore >= 40 ? '#eab308'
                  : badgeScore >= 20 ? '#f97316'
                  : '#ef4444';
                labelColor = badgeColor;
              } else if (isSingle && singleComp === 'ai_tampered') {
                badgeScore = item.risk_score ?? 0;
                label = t('pipeline.componentNames.aiTamper', 'AI detection');
                badgeColor = badgeScore >= 80 ? '#ef4444'
                  : badgeScore >= 60 ? '#f97316'
                  : badgeScore >= 40 ? '#eab308'
                  : '#22c55e';
                labelColor = badgeColor;
              } else if (isSingle && singleComp === 'techniques') {
                badgeScore = item.risk_score ?? 0;
                label = t('pipeline.componentNames.techniques', 'Manipulation techniques');
                badgeColor = badgeScore >= 70 ? '#ef4444'
                  : badgeScore >= 40 ? '#f97316'
                  : badgeScore >= 20 ? '#eab308'
                  : '#22c55e';
                labelColor = badgeColor;
              } else if (isSingle && singleComp === 'claims') {
                badgeScore = item.risk_score ?? 0;
                label = t('pipeline.componentNames.claims', 'Claim verification');
                badgeColor = '#a78bfa';
                labelColor = '#a78bfa';
              } else {
                // Pipeline (multi-component) — existing logic
                badgeScore = item.risk_score != null && item.risk_score > 0 ? item.risk_score : 0;
                label = item.risk_category
                  ? enumLabel(item.risk_category || '').replace(/_/g, ' ')
                  : enumLabel(item.risk_level || '') || 'Analysis';
                badgeColor = badgeScore > 0
                  ? getRiskColor(item.risk_level || '', item.risk_category || '')
                  : '#8b5cf6';
                labelColor = badgeColor;
              }
              return (
                <HistoryCard key={item.session_id} onClick={() => loadDetail(item.session_id)}>
                  <CardTop>
                    <CardLeft>
                      <RiskBadge color={isRunning ? '#8b5cf6' : badgeColor}>
                        {isRunning ? <CardSpinner /> : (badgeScore > 0 ? Math.round(badgeScore) : '--')}
                      </RiskBadge>
                      <CardInfo>
                        <CardCategory color={isRunning ? '#a78bfa' : labelColor}>
                          {isRunning ? t('history.statusRunning') : label}
                        </CardCategory>
                        <CardPreview>{item.input_text || item.input_url || ''}</CardPreview>
                      </CardInfo>
                    </CardLeft>
                    <CardRight>
                      <CardDate>{formatDate(item.started_at)}</CardDate>
                      <CardActions>
                        {!isRunning && item.status === 'completed' && (
                          <DownloadPdfButtonLazy sessionId={item.session_id} compact />
                        )}
                        <DeleteBtn
                          onClick={e => { e.stopPropagation(); handleDelete(item.session_id); }}
                          confirm={deleteConfirm === item.session_id}
                        >
                          {deleteConfirm === item.session_id ? t('common.confirmQuestion') : t('common.delete')}
                        </DeleteBtn>
                      </CardActions>
                    </CardRight>
                  </CardTop>
                  <CardBottom>
                    {isRunning ? (
                      <>
                        {progress ? (
                          <>
                            <CardChip color="#8b5cf6">{t('history.componentsProgress', { completed: progress.completed_components.length, total: progress.total_components })}</CardChip>
                            <CardChip color="#a78bfa">{Math.round(progress.progress)}%</CardChip>
                            <CardChip color="var(--fg-muted)">{(progress.elapsed_ms / 1000).toFixed(0)}s</CardChip>
                          </>
                        ) : (
                          <CardChip color="#8b5cf6">{t('history.starting')}</CardChip>
                        )}
                        <TypeChip>{item.input_type}</TypeChip>
                      </>
                    ) : (
                      <>
                        {!isSingle && item.risk_level && <CardChip color={getRiskColor(item.risk_level, item.risk_category || '')}>{enumLabel(item.risk_level)}</CardChip>}
                        {!isSingle && item.components_run && item.components_run.length > 0 && (
                          <CardChip color="#8b5cf6">{t('history.componentsProgress', { completed: item.components_run.length, total: item.components_run.length })}</CardChip>
                        )}
                        {item.total_duration_ms != null && item.total_duration_ms > 0 && (
                          <CardChip color="var(--fg-muted)">{(item.total_duration_ms / 1000).toFixed(1)}s</CardChip>
                        )}
                        <TypeChip>{item.input_type}</TypeChip>
                      </>
                    )}
                  </CardBottom>
                  {isRunning && (
                    <CardProgressBar>
                      <CardProgressFill width={progress ? progress.progress : 0} />
                    </CardProgressBar>
                  )}
                </HistoryCard>
              );
            })}
          </ItemsList>

          {pagination && pagination.pages > 1 && (
            <PaginationRow>
              <PaginationBtn disabled={pagination.page <= 1} onClick={() => setPage(p => p - 1)}>
                {t('common.previous')}
              </PaginationBtn>
              <PaginationInfo>{t('history.pageInfo', { page: pagination.page, pages: pagination.pages, total: pagination.total })}</PaginationInfo>
              <PaginationBtn disabled={pagination.page >= pagination.pages} onClick={() => setPage(p => p + 1)}>
                {t('common.next')}
              </PaginationBtn>
            </PaginationRow>
          )}
        </>
      )}

    </Container>
  );
};

export default History;

// =============================================================================
// Styled Components
// =============================================================================

const Container = styled.div`
  width: 100%;
  max-width: 1800px;
  margin: 0 auto;
  padding: 0;
  overflow: hidden;
  box-sizing: border-box;
  @media (max-width: 768px) { padding: 0; }
`;

const PageHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: ${spacing.xl}px; gap: 12px; flex-wrap: wrap;
`;
const PageTitle = styled.h1`
  font-family: ${typography.fontFamily.primary}; font-size: 28px;
  font-weight: ${typography.fontWeight.bold}; color: #ffffff; margin: 0;
  [data-theme="light"] & { color: ${colors.slateCharcoal}; }
`;
const RefreshBtn = styled.button`
  padding: 8px 20px; border: 1px solid rgba(139,92,246,0.3); border-radius: 8px;
  background: transparent; color: #a78bfa; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all 0.2s;
  &:hover { background: rgba(139,92,246,0.1); border-color: rgba(139,92,246,0.5); }
  [data-theme="light"] & { color: ${colors.neonViolet}; border-color: rgba(139,92,246,0.2);
    &:hover { background: rgba(139,92,246,0.05); } }
`;

const StatusMsg = styled.div`
  text-align: center; padding: 60px 20px; font-size: 14px;
  color: rgba(255,255,255,0.4);
  [data-theme="light"] & { color: ${colors.hintGray}; }
`;
const ErrorMsg = styled.div`
  text-align: center; padding: 20px; font-size: 14px; color: #f87171;
  background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.2); border-radius: 10px;
`;

/* History List */
const ItemsList = styled.div`display: flex; flex-direction: column; gap: 8px;`;

const HistoryCard = styled.div`
  padding: 14px 18px; background: rgba(255,255,255,0.02);
  border: 1px solid rgba(255,255,255,0.06); border-radius: 10px;
  cursor: pointer; transition: all 0.2s; overflow: hidden;
  &:hover { background: rgba(255,255,255,0.04); border-color: rgba(139,92,246,0.2); }
  [data-theme="light"] & { background: white; border-color: ${colors.lightGray};
    &:hover { border-color: rgba(139,92,246,0.3); } }
`;
const CardTop = styled.div`
  display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 10px;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
  }
`;
const CardLeft = styled.div`
  display: flex; align-items: center; gap: 12px; flex: 1; min-width: 0;

  @media (max-width: 480px) {
    width: 100%;
  }
`;
const CardRight = styled.div`
  display: flex; flex-direction: column; align-items: flex-end; gap: 6px; flex-shrink: 0;

  @media (max-width: 480px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
`;

const RiskBadge = styled.div<{ color: string }>`
  width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center;
  font-size: 16px; font-weight: 800; color: ${p => p.color}; background: ${p => p.color}15;
  flex-shrink: 0;
`;
const CardInfo = styled.div`min-width: 0; flex: 1;`;
const CardCategory = styled.div<{ color: string }>`
  font-size: 13px; font-weight: 700; color: ${p => p.color}; text-transform: uppercase; margin-bottom: 2px;
`;
const CardPreview = styled.div`
  font-size: 12px; color: rgba(255,255,255,0.45); overflow: hidden; text-overflow: ellipsis;
  white-space: nowrap; max-width: 100%;
  [data-theme="light"] & { color: ${colors.steelGray}; }
`;
const CardDate = styled.div`
  font-size: 11px; color: rgba(255,255,255,0.3);
  [data-theme="light"] & { color: ${colors.hintGray}; }
`;
const CardActions = styled.div`display: flex; gap: 6px;`;

const DeleteBtn = styled.button<{ confirm?: boolean }>`
  padding: 6px 12px; border-radius: 5px; font-size: 10px; font-weight: 600; cursor: pointer;
  border: 1px solid ${p => p.confirm ? 'rgba(234,179,8,0.4)' : 'rgba(239,68,68,0.3)'};
  background: ${p => p.confirm ? 'rgba(234,179,8,0.08)' : 'rgba(239,68,68,0.06)'};
  color: ${p => p.confirm ? '#fbbf24' : '#f87171'};
  min-height: 32px;
  &:hover { background: ${p => p.confirm ? 'rgba(234,179,8,0.15)' : 'rgba(239,68,68,0.12)'}; }

  @media (max-width: 480px) {
    min-height: 36px;
    padding: 6px 16px;
  }
`;

const CardBottom = styled.div`display: flex; gap: 6px; flex-wrap: wrap;`;
const CardChip = styled.span<{ color: string }>`
  font-size: 10px; font-weight: 600; padding: 2px 8px; border-radius: 4px;
  background: ${p => p.color.startsWith('var(') ? 'var(--bg-hover)' : `${p.color}12`};
  color: ${p => p.color};
`;
const TypeChip = styled.span`
  font-size: 10px; font-weight: 600; padding: 2px 8px; border-radius: 4px;
  background: rgba(139,92,246,0.1); color: #a78bfa; text-transform: uppercase;
  [data-theme="light"] & { background: rgba(139,92,246,0.06); color: ${colors.neonViolet}; }
`;

/* Running session animations & components */
const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;

const CardSpinner = styled.div<{ size?: number }>`
  width: ${p => p.size || 18}px;
  height: ${p => p.size || 18}px;
  border: 2px solid rgba(139,92,246,0.2);
  border-top-color: #a78bfa;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  flex-shrink: 0;
`;

const CardProgressBar = styled.div`
  height: 3px;
  background: rgba(139,92,246,0.1);
  border-radius: 0 0 10px 10px;
  margin: 10px -18px -14px -18px;
  overflow: hidden;
`;
const CardProgressFill = styled.div<{ width: number }>`
  height: 100%;
  width: ${p => Math.min(p.width, 100)}%;
  background: linear-gradient(90deg, #8b5cf6, #a78bfa);
  border-radius: 0 0 10px 10px;
  transition: width 0.5s ease;
`;

const RunningDetailBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  background: rgba(139,92,246,0.06);
  border: 1px solid rgba(139,92,246,0.15);
  border-radius: 12px;
  animation: ${pulse} 2s ease-in-out infinite;
  [data-theme="light"] & { background: rgba(139,92,246,0.04); border-color: rgba(139,92,246,0.12); }
`;
const RunningDetailText = styled.div`
  display: flex; flex-direction: column; gap: 8px; flex: 1; min-width: 0;
`;
const RunningDetailTitle = styled.div`
  font-size: 14px; font-weight: 700; color: #a78bfa;
  [data-theme="light"] & { color: ${colors.neonViolet}; }
`;
const RunningDetailMeta = styled.div`
  font-size: 12px; color: rgba(255,255,255,0.4);
  [data-theme="light"] & { color: ${colors.steelGray}; }
`;
const RunningProgressBar = styled.div`
  height: 4px; background: rgba(139,92,246,0.1); border-radius: 2px; overflow: hidden; width: 100%;
  [data-theme="light"] & { background: rgba(139,92,246,0.08); }
`;
const RunningProgressFill = styled.div<{ width: number }>`
  height: 100%; width: ${p => Math.min(p.width, 100)}%; background: linear-gradient(90deg, #8b5cf6, #a78bfa);
  border-radius: 2px; transition: width 0.5s ease;
`;
const RunningComponentsList = styled.div`
  display: flex; gap: 6px; flex-wrap: wrap;
`;

/* Modal */
const ModalOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.85); backdrop-filter: blur(8px);
  display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px;

  @media (max-width: 480px) {
    padding: 0;
  }
`;
const ModalContent = styled.div`
  background: #0a0a1a; border: 1px solid rgba(139,92,246,0.2); border-radius: 16px;
  max-width: 1600px; width: 95%; max-height: 90vh; overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.5);
  display: flex; flex-direction: column;
  [data-theme="light"] & { background: white; border-color: ${colors.lightGray}; }

  @media (max-width: 480px) {
    max-height: 100vh;
    border-radius: 0;
    max-width: 100%;
    width: 100%;
    height: 100%;
    border: none;
  }
`;
const ModalHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px 24px; border-bottom: 1px solid rgba(255,255,255,0.06);
  flex-shrink: 0;
  [data-theme="light"] & { border-bottom-color: ${colors.lightGray}; }

  @media (max-width: 480px) {
    padding: 12px 16px;
    position: sticky;
    top: 0;
    background: #0a0a1a;
    z-index: 1;
    [data-theme="light"] & { background: white; }
  }
`;
const ModalTitle = styled.h2`
  font-size: 18px; font-weight: 700; color: #fff; margin: 0;
  [data-theme="light"] & { color: ${colors.slateCharcoal}; }
`;
const ModalHeaderRight = styled.div`display: flex; align-items: center; gap: 10px;`;
const CloseBtn = styled.button`
  width: 38px; height: 38px; border-radius: 10px; border: 1px solid rgba(139,92,246,0.4);
  background: rgba(139,92,246,0.15); color: #a78bfa; font-size: 20px; cursor: pointer;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  font-weight: 300; transition: all 0.2s;
  &:hover { background: rgba(139,92,246,0.25); color: white; border-color: rgba(139,92,246,0.6); }
  [data-theme="light"] & { border-color: rgba(139,92,246,0.3); color: ${colors.neonViolet};
    background: rgba(139,92,246,0.08);
    &:hover { background: rgba(139,92,246,0.15); color: ${colors.slateCharcoal}; } }

  @media (max-width: 480px) {
    width: 44px; height: 44px; font-size: 24px;
    background: rgba(139,92,246,0.2); border-color: rgba(139,92,246,0.5);
  }
`;
const ModalBody = styled.div`
  padding: 24px; overflow-y: auto; flex: 1;
  display: flex; flex-direction: column; gap: 24px;

  @media (max-width: 480px) {
    padding: 16px;
  }
`;

/* Sections */
const Section = styled.div`display: flex; flex-direction: column; gap: 10px;`;
const SectionLabel = styled.div`
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 700;
  color: rgba(255,255,255,0.35);
  [data-theme="light"] & { color: ${colors.steelGray}; }
`;

const InputPreviewBlock = styled.div`
  display: flex; flex-direction: column; gap: 6px;
  padding: 14px 18px;
  background: rgba(255,255,255,0.018);
  border: 1px solid rgba(255,255,255,0.04);
  border-radius: 14px;
  margin-bottom: ${spacing.md}px;
  [data-theme="light"] & { background: rgba(0,0,0,0.015); border-color: rgba(0,0,0,0.05); }
`;
const InputPreviewLabel = styled.div`
  font-size: 10.5px; font-weight: 600; letter-spacing: 0.16em; text-transform: uppercase;
  color: rgba(255,255,255,0.45);
  [data-theme="light"] & { color: ${colors.steelGray}; }
`;
const InputPreviewText = styled.div`
  font-size: 13px; color: rgba(255,255,255,0.7); line-height: 1.6;
  word-break: break-word; overflow-wrap: break-word;
  max-height: 240px; overflow-y: auto;
  display: flex; flex-direction: column; gap: 10px;
  & p { margin: 0; }
  [data-theme="light"] & { color: ${colors.slateCharcoal}; }
`;

/* Detail view header (back btn + delete) */
const DetailHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  margin-bottom: ${spacing.lg}px;
`;
const BackBtn = styled.button`
  display: inline-flex; align-items: center; gap: 8px;
  padding: 9px 16px; background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08); border-radius: 10px;
  color: rgba(255,255,255,0.85); cursor: pointer;
  font-family: ${typography.fontFamily.primary}; font-size: 13.5px; font-weight: 500;
  letter-spacing: -0.005em;
  transition: all 0.18s ease;
  &:hover { background: rgba(255,255,255,0.06); border-color: rgba(255,255,255,0.14); }
  [data-theme="light"] & {
    background: white; border-color: rgba(0,0,0,0.08); color: ${colors.slateCharcoal};
    &:hover { background: rgba(0,0,0,0.02); border-color: rgba(0,0,0,0.14); }
  }
`;
const DeleteIconBtn = styled.button<{ confirm: boolean }>`
  display: inline-flex; align-items: center; gap: 8px;
  padding: 9px 14px;
  background: ${p => p.confirm ? 'rgba(239,68,68,0.15)' : 'transparent'};
  border: 1px solid ${p => p.confirm ? 'rgba(239,68,68,0.4)' : 'rgba(239,68,68,0.25)'};
  border-radius: 10px;
  color: ${p => p.confirm ? '#fca5a5' : '#f87171'};
  cursor: pointer;
  font-family: ${typography.fontFamily.primary}; font-size: 13px; font-weight: 500;
  transition: all 0.18s ease;
  &:hover { background: rgba(239,68,68,0.12); border-color: rgba(239,68,68,0.4); }
  [data-theme="light"] & { color: #dc2626; border-color: rgba(220,38,38,0.3); }
`;

/* Verdict in modal */
const VerdictRow = styled.div`
  display: flex; align-items: center; gap: 20px; padding: 16px;
  background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px;
  [data-theme="light"] & { background: ${colors.warmGray}; border-color: ${colors.lightGray}; }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;
const VerdictScoreRing = styled.div`flex-shrink: 0;`;
const RingCircle = styled.div<{ color: string; percent: number }>`
  width: 70px; height: 70px; position: relative; color: ${p => p.color};
  svg { width: 100%; height: 100%; transform: rotate(-90deg); }
`;
const RingValue = styled.span`
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  font-size: 20px; font-weight: 800; color: inherit;
`;
const VerdictDetails = styled.div`display: flex; flex-direction: column; gap: 4px; min-width: 0; flex: 1;`;
const VerdictCategory = styled.div<{ color: string }>`
  font-size: 18px; font-weight: 800; color: ${p => p.color}; text-transform: uppercase;
  word-break: break-word; overflow-wrap: break-word;
`;
const VerdictMeta = styled.div`
  font-size: 12px; color: rgba(255,255,255,0.4); word-break: break-word;
  [data-theme="light"] & { color: ${colors.steelGray}; }
`;
const VerdictAction = styled.span`
  font-size: 10px; font-weight: 700; padding: 3px 10px; border-radius: 4px;
  background: rgba(139,92,246,0.12); color: #a78bfa; text-transform: uppercase;
  width: fit-content;
  [data-theme="light"] & { background: rgba(139,92,246,0.08); color: ${colors.neonViolet}; }
`;

const VerdictExplanation = styled.div`
  font-size: 13px; line-height: 1.6; color: rgba(255,255,255,0.6);
  padding: 12px 16px; background: rgba(139,92,246,0.06);
  border: 1px solid rgba(139,92,246,0.12); border-radius: 8px;
  border-left: 3px solid rgba(139,92,246,0.4);
  [data-theme="light"] & { background: rgba(139,92,246,0.03); color: ${colors.steelGray}; border-color: rgba(139,92,246,0.1); border-left-color: ${colors.neonViolet}; }
`;

const ScoresGrid = styled.div`display: flex; flex-direction: column; gap: 8px;`;
const ScoreItem = styled.div`display: flex; align-items: center; gap: 10px;`;
const ScoreLabel = styled.span`
  font-size: 12px; color: rgba(255,255,255,0.5); width: 90px; flex-shrink: 0; text-transform: capitalize;
  [data-theme="light"] & { color: ${colors.slateCharcoal}; }

  @media (max-width: 480px) {
    width: 70px;
    font-size: 11px;
  }
`;
const ScoreBarBg = styled.div`
  flex: 1; height: 6px; background: rgba(255,255,255,0.06); border-radius: 3px; overflow: hidden;
  [data-theme="light"] & { background: ${colors.lightGray}; }
`;
const ScoreBarFill = styled.div<{ width: number; color: string }>`
  height: 100%; width: ${p => Math.min(p.width, 100)}%; background: ${p => p.color};
  border-radius: 3px; transition: width 0.3s;
`;
const ScoreVal = styled.span`
  font-size: 12px; font-weight: 700; color: rgba(255,255,255,0.7); width: 28px; text-align: right;
  [data-theme="light"] & { color: ${colors.slateCharcoal}; }
`;

/* Virality Assessment */
const ViralityCard = styled.div`
  padding: 14px; background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06); border-radius: 10px;
  display: flex; flex-direction: column; gap: 12px; margin-top: 4px;
  [data-theme="light"] & { background: white; border-color: ${colors.lightGray}; }
`;
const ViralityHeader = styled.div`
  display: flex; align-items: center; gap: 12px;
`;
const ViralityRing = styled.div`flex-shrink: 0;`;
const ViralityCircle = styled.div<{ color: string; percent: number }>`
  width: 50px; height: 50px; position: relative; color: ${p => p.color};
  svg { width: 100%; height: 100%; transform: rotate(-90deg); }
`;
const ViralityValue = styled.span`
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  font-size: 14px; font-weight: 800; color: inherit;
`;
const ViralityInfo = styled.div`display: flex; flex-direction: column; gap: 4px;`;
const ViralityTitle = styled.div`
  font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;
  color: rgba(255,255,255,0.4); font-weight: 600;
  [data-theme="light"] & { color: ${colors.steelGray}; }
`;
const ViralityLevelBadge = styled.span<{ color: string }>`
  display: inline-block; font-size: 11px; font-weight: 700; padding: 3px 10px;
  border-radius: 4px; background: ${p => p.color}18; color: ${p => p.color};
  text-transform: uppercase; letter-spacing: 0.5px; width: fit-content;
`;
const ViralityFactors = styled.div`
  display: flex; flex-direction: column; gap: 8px;
  border-top: 1px solid rgba(255,255,255,0.04); padding-top: 10px;
  [data-theme="light"] & { border-top-color: ${colors.lightGray}; }
`;

/* Skipped/empty placeholder + verdict override badge — renderer-ele detaliate sunt importate din PipelineAnalysis */
const SkippedMsg = styled.div`
  font-size: 12px; color: rgba(255,255,255,0.3); padding: 10px 14px;
  background: rgba(255,255,255,0.02); border: 1px dashed rgba(255,255,255,0.08);
  border-radius: 8px; font-style: italic;
  [data-theme="light"] & { color: ${colors.hintGray}; background: ${colors.warmGray}; border-color: ${colors.lightGray}; }
`;
const Badge = styled.span<{ color: string }>`
  font-size: 10px; font-weight: 600; padding: 2px 8px; border-radius: 4px;
  background: ${p => p.color}15; color: ${p => p.color}; white-space: nowrap;
`;

/* Metadata */
const MetaRow = styled.div`display: flex; gap: 8px; flex-wrap: wrap;`;
const MetaChip = styled.span`
  font-size: 11px; padding: 4px 10px; border-radius: 6px;
  background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.4);
  word-break: break-word; overflow-wrap: break-word;
  [data-theme="light"] & { background: ${colors.warmGray}; color: ${colors.steelGray}; }
`;

/* Pagination */
const PaginationRow = styled.div`
  display: flex; justify-content: center; align-items: center; gap: 16px;
  margin-top: ${spacing.lg}px; padding: ${spacing.md}px 0; flex-wrap: wrap;
`;
const PaginationBtn = styled.button<{ disabled?: boolean }>`
  padding: 6px 16px; border: 1px solid rgba(139,92,246,0.3); border-radius: 6px;
  background: transparent; color: #a78bfa; font-size: 12px; font-weight: 600;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${p => p.disabled ? 0.3 : 1};
  transition: all 0.2s;
  &:hover:not(:disabled) { background: rgba(139,92,246,0.1); border-color: rgba(139,92,246,0.5); }
  [data-theme="light"] & { color: ${colors.neonViolet}; border-color: rgba(139,92,246,0.2);
    &:hover:not(:disabled) { background: rgba(139,92,246,0.05); } }
`;
const PaginationInfo = styled.span`
  font-size: 12px; color: rgba(255,255,255,0.4);
  [data-theme="light"] & { color: ${colors.steelGray}; }
`;

